/**
 * lib/studentDiscounts.ts
 * ------------------------------------------------------------------
 * MANUAL, PER-PUPIL DISCOUNTS — the bursar's own instrument.
 *
 * WHAT THIS REPLACES. The sibling discount used to be automatic: group
 * pupils into a family, rank them by age, approve the family, and the
 * billing engine took a percentage off everyone from the second child down.
 * Three moving parts had to be right before a single pula came off a bill,
 * and the first of them — the family — was built partly from SURNAME, which
 * had put five unrelated Molefes in one household and thirteen unrelated
 * pupils in another. A discount engine standing on that was never going to
 * be trusted, and it was not: zero discounts had ever been applied.
 *
 * So the school asked for the opposite. The bursar finds a pupil and says
 * what they get. No inference, no ranking, no approval queue.
 *
 * PERCENT OR AMOUNT, because both are real instructions a school gives.
 * "10% off tuition" scales with the fee; "P1,500 off" is what gets agreed in
 * a meeting with a parent. Storing the INSTRUCTION rather than the resulting
 * figure means a percentage keeps working when fees change, and an amount
 * stays exactly the amount that was promised.
 *
 * HOW IT LINES UP WITH THE LEDGER. A discount here is not a payment and not
 * a write-off: it is revenue the school chose not to charge. It becomes a
 * line in `invoice.discounts[]` carrying a DiscountKind, so the existing
 * posting engine handles it with no new code — discountLines() maps the kind
 * through the posting map to 4900 Discounts & Bursaries Granted and posts
 * Dr 4900 / Cr Accounts Receivable. Contra-revenue, which is why 4900 is
 * typed `income` and carries a debit balance.
 *
 * That only holds for invoices RAISED AFTER the discount is recorded. An
 * invoice already issued has already posted at its full amount, and reducing
 * it after the fact is a credit note — a different event, with its own
 * number, its own audit trail and its own journal. The screen offers that as
 * a separate, explicit step rather than pretending one thing is the other.
 * ------------------------------------------------------------------ */
import type { DiscountKind, FeeCategory, ManualDiscount } from '@/lib/financeCore';
import { round2 } from '@/lib/financeCore';

export const STUDENT_DISCOUNTS_COLLECTION = 'financeStudentDiscounts';

/** How the discount is expressed. */
export type DiscountMode = 'percent' | 'amount';

/** One credit raised from a concession, against one invoice. */
export interface AppliedCredit {
  invoiceId: string;
  invoiceNo?: string;
  /** The credit note this produced — the audit link back to the ledger. */
  adjustmentId: string;
  adjustmentNo?: string;
  amount: number;
  at: string;
  by?: string;
}

export interface StudentDiscount {
  id: string;
  studentId: string;
  /** Denormalised so the list reads without joining the whole roll. */
  studentName?: string;
  /**
   * Which kind of concession this is. Drives BOTH the label a parent sees on
   * the invoice and the account it posts to, so it is not cosmetic.
   * 'sibling' is the common case and the reason this screen exists.
   */
  kind: DiscountKind;
  mode: DiscountMode;
  /** Percent 0–100, or an amount in pula. */
  value: number;
  /** What the discount bites on. Sibling concessions are tuition-only by
   *  convention here; 'all' takes it off the whole bill. */
  appliesToCategory: FeeCategory | 'all';
  /** Why the school gave this up. The whole audit value of the record. */
  reason: string;
  /**
   * ONCE-OFF, AND SPENT WHEN IT IS APPLIED.
   *
   * A sibling discount at LKC is a single reduction against a single bill,
   * not a standing arrangement that quietly reduces every future invoice.
   * Recording one and applying it are the same act — see recordAndApply.
   *
   * `active` therefore means "recorded and not yet spent". It goes false
   * the moment the credit note is raised, and an unspent one is a record
   * that has done nothing to any bill: on 5 September the school found
   * three of them, worth P4,000, sitting in exactly that state because
   * recording used to be a separate step from applying.
   */
  active: boolean;
  /**
   * Invoices this concession has already been credited against.
   *
   * THE GUARD THAT MATTERS. The school bills at the full fee and discounts
   * afterwards, so applying a concession is a manual act the bursar performs
   * per invoice — and a manual act is one that can be performed twice. A
   * 10% sibling discount credited twice against the same invoice is money
   * out of the school with two credit notes and nothing to say it was one
   * concession. Recorded here so the screen can refuse.
   */
  appliedTo?: AppliedCredit[];
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  endedAt?: string;
  endedBy?: string;
  endedReason?: string;
}

export const DISCOUNT_MODE_LABELS: Record<DiscountMode, string> = {
  percent: 'Percentage',
  amount: 'Fixed amount',
};

/**
 * Reject a discount the bursar should not be able to save.
 * Returns null when it is fine.
 */
/**
 * Recorded, but never applied to anything.
 *
 * The state the school found three concessions in. Worth naming because it
 * is invisible otherwise — the record looks complete, and only the absence
 * of a credit note says it did nothing.
 */
export function isUnapplied(d: Pick<StudentDiscount, 'appliedTo'>): boolean {
  return (d.appliedTo ?? []).length === 0;
}

/** What this concession actually took off a bill, across every application. */
export function creditedTotal(d: Pick<StudentDiscount, 'appliedTo'>): number {
  return round2((d.appliedTo ?? []).reduce((a, c) => a + (Number(c.amount) || 0), 0));
}

export function validateDiscount(input: {
  mode: DiscountMode;
  value: number;
  reason?: string;
}): string | null {
  const v = Number(input.value);
  if (!Number.isFinite(v) || v <= 0) return 'Enter an amount greater than zero.';
  if (input.mode === 'percent' && v > 100) return 'A percentage cannot be more than 100.';
  // A percentage may carry decimals; a fraction of a thebe may not.
  if (input.mode === 'amount' && round2(v) !== v) {
    return 'An amount can have at most two decimal places.';
  }
  if (String(input.reason ?? '').trim().length < 3) {
    return 'Give a reason. It is the record of why the school gave this up.';
  }
  return null;
}

/** What the billing engine needs. The arithmetic lives in financeCore,
 *  which owns the invoice shape; this module owns the document. */
export type ActiveDiscount = ManualDiscount;

/** Reduce a stored record to what the engine consumes, with its label. */
export function toEngineDiscount(d: StudentDiscount): ActiveDiscount {
  return {
    kind: d.kind,
    mode: d.mode,
    value: d.value,
    appliesToCategory: d.appliesToCategory,
    label: defaultLabel(d),
  };
}

/** Has this concession already been credited against this invoice? */
export function alreadyApplied(d: StudentDiscount, invoiceId: string): AppliedCredit | null {
  return (d.appliedTo ?? []).find((a) => a.invoiceId === invoiceId) ?? null;
}

/** "Sibling discount (10%)" / "Sibling discount (P1,500.00)". */
export function defaultLabel(d: ActiveDiscount): string {
  const base = LABEL_BY_KIND[d.kind] ?? 'Discount';
  return d.mode === 'percent'
    ? `${base} (${d.value}%)`
    : `${base} (P${round2(d.value).toFixed(2)})`;
}

const LABEL_BY_KIND: Record<string, string> = {
  sibling: 'Sibling discount',
  bursary: 'Bursary',
  staff: 'Staff discount',
  prepayment: 'Prepayment discount',
  manual: 'Discount',
};


/* ================== discount, or credit note? ====================== */

/**
 * WHICH INSTRUMENT REDUCES THIS BILL.
 *
 * The school enters sibling discounts by hand and means a DISCOUNT: money
 * off the fee, shown on the bill as money off the fee. It should not come
 * out the other side as a credit note reversing a charge, which is what
 * happened to ADJ/2026/0001 — a P1,000 sibling discount that reached the
 * family as a credit note against an opening balance.
 *
 * There is exactly one condition under which it must still be a credit
 * note, and it is not a matter of preference. Once an invoice has POSTED,
 * a journal exists saying the school billed the full amount. A journal is
 * not edited; it is reversed by another journal. Quietly reducing the
 * invoice behind a posted journal leaves the sub-ledger and the GL
 * disagreeing, which is the one failure the whole double-entry design is
 * there to prevent.
 *
 * So the question is factual, not editorial: HAS THIS INVOICE POSTED?
 *
 *   not posted  → discount. Nothing to contradict. When the cut-over comes
 *                 the invoice posts once, net, with its discount line going
 *                 to 4900 Discounts & Bursaries Granted.
 *   posted      → credit note, exactly as before.
 *
 * Today every LKC invoice is in the first case; `financeGlJournals` is
 * empty and the GL cut-over date has not been set. After cut-over this
 * returns 'credit_note' on its own, with nothing to remember to change.
 */
export type DiscountRoute = 'discount' | 'credit_note';

export interface RouteDecision {
  route: DiscountRoute;
  /** Said in the bursar's terms, on the screen, before they commit. */
  because: string;
}

export function discountRoute(posted: boolean): RouteDecision {
  return posted
    ? {
      route: 'credit_note',
      because: 'This invoice has already posted to the ledger, so the reduction is raised '
        + 'as a credit note with its own number and journal.',
    }
    : {
      route: 'discount',
      because: 'This goes on the invoice as a discount, so the bill shows the reduced fee.',
    };
}

/** The line a concession becomes on an invoice. */
export function toInvoiceDiscount(
  d: Pick<StudentDiscount, 'kind' | 'mode' | 'value' | 'appliesToCategory' | 'reason' | 'id'>,
  amount: number,
): { ruleId?: string; kind: DiscountKind; label: string; amount: number; appliesToCategory: FeeCategory | 'all' } {
  return {
    ...(d.id && d.id !== 'preview' ? { ruleId: d.id } : {}),
    kind: d.kind,
    label: `${defaultLabel(toEngineDiscount(d as StudentDiscount))}${d.reason ? ` — ${d.reason}` : ''}`,
    amount: round2(amount),
    appliesToCategory: d.appliesToCategory,
  };
}
